/**
 * CRM Data Service
 * Frontend service for all CRM API calls
 * Matches backend routes structure in /backend/routes/crm/
 */

import apiClient from '../../../../apiClient';

class CRMDataService {
  // ===================================
  // DROPDOWN ENDPOINTS (/api/crm/dropdown)
  // ===================================

  async getDropdownData() {
    try {
      const response = await apiClient.get('/crm/dropdown/complete');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      throw error;
    }
  }

  async getStores() {
    try {
      const response = await apiClient.get('/crm/dropdown/stores');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching stores:', error);
      throw error;
    }
  }

  async getCategoriesByTeam(teamId) {
    try {
      const response = await apiClient.get(`/crm/dropdown/teams/${teamId}/categories`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  async getSubcategoriesByCategory(categoryId) {
    try {
      const response = await apiClient.get(`/crm/dropdown/categories/${categoryId}/subcategories`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      throw error;
    }
  }

  async getMentionableUsers() {
    try {
      const response = await apiClient.get('/crm/dropdown/mentionable-users');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching mentionable users:', error);
      throw error;
    }
  }

  async getHrGeneralists() {
    try {
      const response = await apiClient.get('/crm/dropdown/hr-generalists');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching HR generalists:', error);
      return [];
    }
  }

  // ===================================
  // TEAM ENDPOINTS (/api/crm/teams)
  // ===================================

  async getTeams() {
    try {
      const response = await apiClient.get('/crm/teams');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching teams:', error);
      throw error;
    }
  }

  async getTeamById(teamId) {
    try {
      const response = await apiClient.get(`/crm/teams/${teamId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching team:', error);
      throw error;
    }
  }

  async getTeamMembers(teamCode) {
    try {
      const response = await apiClient.get(`/crm/teams/${teamCode}/members`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching team members:', error);
      throw error;
    }
  }

  // ===================================
  // TICKET ENDPOINTS (/api/crm/tickets)
  // ===================================

  async createTicket(ticketData, files = [], onProgress = null) {
    try {
      const userEmail = localStorage.getItem('userEmail') || 'default@cafuamanagement.com';
      const payload = {
        ...ticketData,
        requesterEmail: ticketData.requesterEmail || userEmail,
        requesterName: ticketData.requesterName || 'User',
        conditionalFields: ticketData.conditionalFields || {}
      };

      // If files are provided, use multipart FormData
      if (files && files.length > 0) {
        const formData = new FormData();
        formData.append('ticketData', JSON.stringify(payload));
        files.forEach(file => formData.append('files', file));

        const response = await apiClient.post('/crm/tickets', formData, {
          headers: { 'Content-Type': undefined },
          onUploadProgress: (e) => {
            if (!e.total || !onProgress) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            onProgress(pct, e.loaded, e.total);
          },
        });
        return response.data.data;
      }

      // No files — plain JSON (backward compatible)
      const response = await apiClient.post('/crm/tickets', payload);
      return response.data.data;
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  }

  async searchTickets(criteria = {}) {
    try {
      // Filter out empty/null/undefined/'all' values before sending
      const cleanCriteria = {};
      for (const [key, value] of Object.entries(criteria)) {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') {
          // Join arrays as comma-separated strings (e.g., teamCodes: ['HR','CB'] → 'HR,CB')
          cleanCriteria[key] = Array.isArray(value) ? value.join(',') : value;
        }
      }
      const params = new URLSearchParams(cleanCriteria);
      const response = await apiClient.get(`/crm/tickets?${params.toString()}`);
      return {
        tickets: response.data.data,
        hasMore: response.data.hasMore,
      };
    } catch (error) {
      console.error('Error searching tickets:', error);
      throw error;
    }
  }

  /**
   * For an automatic IT ticket: the HR/TA ticket it was created from (number,
   * subcategory, team, status, reduced HR approval steps). null when none.
   */
  async getSourceTicketSummary(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}/source-summary`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching source ticket summary:', error);
      throw error;
    }
  }

  async getTicketByNumber(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }
  }

  async getTicketById(ticketId) {
    try {
      const response = await apiClient.get(`/crm/tickets/id/${ticketId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket comments:', error);
      throw error;
    }
  }

  async assignTicket(ticketNumber, assignData) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/assign`, assignData);
      return response.data.data;
    } catch (error) {
      console.error('Error assigning ticket:', error);
      throw error;
    }
  }

  async resolveTicket(ticketNumber, resolutionData) {
    try {
      const { files, ...data } = resolutionData;

      if (files && files.length > 0) {
        const formData = new FormData();
        formData.append('resolution', data.resolution);
        if (data.cc_emails && data.cc_emails.length > 0) {
          formData.append('cc_emails', JSON.stringify(data.cc_emails));
        }
        files.forEach(file => formData.append('files', file));

        const response = await apiClient.post(
          `/crm/tickets/${ticketNumber}/resolve`,
          formData,
          { headers: { 'Content-Type': undefined } }
        );
        return response.data.data;
      }

      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/resolve`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error resolving ticket:', error);
      throw error;
    }
  }

  async closeTicket(ticketNumber) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/close`);
      return response.data.data;
    } catch (error) {
      console.error('Error closing ticket:', error);
      throw error;
    }
  }

  async escalateTicket(ticketNumber, escalationData) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/escalate`, escalationData);
      return response.data.data;
    } catch (error) {
      console.error('Error escalating ticket:', error);
      throw error;
    }
  }

  async sendTicketReply(ticketNumber, formData) {
    try {
      const response = await apiClient.post(
        `/crm/tickets/${ticketNumber}/reply`,
        formData,
        { headers: { 'Content-Type': undefined } }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending ticket reply:', error);
      throw error;
    }
  }

  async reopenTicket(ticketNumber) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/reopen`);
      return response.data.data;
    } catch (error) {
      console.error('Error reopening ticket:', error);
      throw error;
    }
  }

  async bulkUpdateTickets(ticketNumbers, updates) {
    try {
      const response = await apiClient.post('/crm/tickets/bulk/update', {
        ticketNumbers,
        updates
      });
      return response.data.data;
    } catch (error) {
      console.error('Error bulk updating tickets:', error);
      throw error;
    }
  }

  async deleteTicket(ticketNumber) {
    try {
      const response = await apiClient.delete(`/crm/tickets/${ticketNumber}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting ticket:', error);
      throw error;
    }
  }

  // ===================================
  // APPROVAL ENDPOINTS (/api/crm/approvals)
  // ===================================

  async checkIsApprover() {
    try {
      const response = await apiClient.get('/crm/approvals/is-approver');
      return response.data.data?.isApprover || false;
    } catch (error) {
      console.error('Error checking approver status:', error);
      return false;
    }
  }

  async getPendingApprovals() {
    try {
      const response = await apiClient.get('/crm/approvals/pending');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      throw error;
    }
  }

  /**
   * Get pending approvals for the current logged-in user
   * @returns {Array} Array of pending approval items assigned to current user
   */
  async getMyPendingApprovals() {
    try {
      // The backend /crm/approvals/pending already filters by the authenticated
      // user's email (including shared_approver_emails), so no client-side
      // filtering is needed — just return the results directly.
      const response = await apiClient.get('/crm/approvals/pending');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching my pending approvals:', error);
      throw error;
    }
  }

  /**
   * Get all tickets where the current user is an approver (any step, any status)
   * Returns tickets in the same shape as searchTickets for sidebar compatibility
   */
  async getMyApprovalTickets({ limit = 100, offset = 0, q = null } = {}) {
    try {
      const params = { limit, offset };
      if (q) params.q = q;
      const response = await apiClient.get('/crm/approvals/my-tickets', {
        params,
      });
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching my approval tickets:', error);
      throw error;
    }
  }

  async actOnApproval(ticketNumber, action, justification) {
    try {
      const response = await apiClient.post(`/crm/approvals/${ticketNumber}`, {
        action,
        justification,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error processing approval action:', error);
      throw error;
    }
  }

  /**
   * Submit an approval action (approve/reject) for a ticket
   * @param {string} ticketNumber - The ticket number
   * @param {string} action - 'approve' or 'reject'
   * @param {string} justification - Optional comments/justification
   */
  async submitApprovalAction(ticketNumber, action, justification) {
    try {
      const response = await apiClient.post(`/crm/approvals/${ticketNumber}`, {
        action,
        justification,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error submitting approval action:', error);
      throw error;
    }
  }

  /**
   * Resubmit a rejected ticket for approval
   * @param {string} ticketNumber - The ticket number
   * @param {string} notes - Optional notes for resubmission
   */
  async cancelApprovalProcess(ticketNumber, reason) {
    try {
      const response = await apiClient.post(`/crm/approvals/${ticketNumber}/cancel`, {
        reason,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error cancelling approval process:', error);
      throw error;
    }
  }

  async resubmitForApproval(ticketNumber, notes = '') {
    try {
      const response = await apiClient.post(`/crm/approvals/${ticketNumber}/resubmit`, {
        notes,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error resubmitting ticket for approval:', error);
      throw error;
    }
  }

  /**
   * Get the approval timeline/steps for a ticket
   * @param {string} ticketNumber - The ticket number
   * @returns {Array} Array of approval steps with status
   */
  async getApprovalTimeline(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/approvals/${ticketNumber}/timeline`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching approval timeline:', error);
      throw error;
    }
  }

  /**
   * Reassign a pending approval step to a new approver
   * @param {number} stepId - The approval step ID
   * @param {Object} data - { new_approver_name, new_approver_email, reason }
   * @returns {Object} Updated step data
   */
  async reassignApprovalStep(stepId, data) {
    try {
      const response = await apiClient.put(`/crm/approvals/reassign-step/${stepId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error reassigning approval step:', error);
      throw error;
    }
  }

  /**
   * Get available approvers by role key
   * @param {string} roleKey - The role key (DIRECTOR, VP, FLM, MUM, SR_MUM)
   * @returns {Array} Array of { name, email }
   */
  async getApproversByRole(roleKey) {
    try {
      const response = await apiClient.get(`/crm/approvals/approvers-by-role/${roleKey}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching approvers by role:', error);
      throw error;
    }
  }

  // ===================================
  // FIELD ENDPOINTS (/api/crm/fields)
  // ===================================

  async getFieldsBySubcategory(subcategoryId) {
    try {
      const response = await apiClient.get(`/crm/fields/subcategory/${subcategoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching conditional fields:', error);
      throw error;
    }
  }

  async validateFieldValues(subcategoryId, fieldValues) {
    try {
      const response = await apiClient.post('/crm/fields/validate', {
        subcategoryId,
        fieldValues
      });
      return response.data.data;
    } catch (error) {
      console.error('Error validating field values:', error);
      throw error;
    }
  }

  async getTicketFieldValues(ticketId) {
    try {
      const response = await apiClient.get(`/crm/fields/ticket/${ticketId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket field values:', error);
      throw error;
    }
  }

  async createField(fieldData) {
    try {
      const response = await apiClient.post('/crm/fields', fieldData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating field:', error);
      throw error;
    }
  }

  async updateField(fieldId, fieldData) {
    try {
      const response = await apiClient.put(`/crm/fields/${fieldId}`, fieldData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating field:', error);
      throw error;
    }
  }

  async deleteField(fieldId) {
    try {
      const response = await apiClient.delete(`/crm/fields/${fieldId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting field:', error);
      throw error;
    }
  }

  // ===================================
  // DASHBOARD ENDPOINTS (/api/crm/dashboard)
  // ===================================

  async getDashboardSummary(dateRange = {}) {
    try {
      const params = new URLSearchParams(dateRange);
      const response = await apiClient.get(`/crm/dashboard/summary?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      return { total: 0, open: 0, pending: 0, resolved: 0, closed: 0, overdue: 0 };
    }
  }

  async getQuickStats() {
    try {
      const response = await apiClient.get('/crm/dashboard/quick-stats');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching quick stats:', error);
      return {};
    }
  }

  async getTeamStats() {
    try {
      const response = await apiClient.get('/crm/dashboard/team-stats');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching team stats:', error);
      return [];
    }
  }

  async getTicketTrends() {
    try {
      const response = await apiClient.get('/crm/dashboard/trends');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket trends:', error);
      return [];
    }
  }

  async getWorkloadSummary() {
    try {
      const response = await apiClient.get('/crm/dashboard/workload');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching workload summary:', error);
      return [];
    }
  }

  async getSLAOverview() {
    try {
      const response = await apiClient.get('/crm/dashboard/sla');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching SLA overview:', error);
      return {};
    }
  }

  async getAtRiskTickets(pagination = {}) {
    try {
      const params = new URLSearchParams(pagination);
      const response = await apiClient.get(`/crm/dashboard/sla/at-risk?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching at-risk tickets:', error);
      return [];
    }
  }

  async getBreachedTickets(pagination = {}) {
    try {
      const params = new URLSearchParams(pagination);
      const response = await apiClient.get(`/crm/dashboard/sla/breached?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching breached tickets:', error);
      return [];
    }
  }

  async getStoreMetrics(dateRange = {}) {
    try {
      const params = new URLSearchParams(dateRange);
      const response = await apiClient.get(`/crm/dashboard/store-metrics?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching store metrics:', error);
      return [];
    }
  }

  async getPerformanceMetrics(dateRange = {}) {
    try {
      const params = new URLSearchParams(dateRange);
      const response = await apiClient.get(`/crm/dashboard/performance?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      return {};
    }
  }

  async exportDashboardData(format = 'pdf', dateRange = {}) {
    try {
      const params = new URLSearchParams({ format, ...dateRange });
      const response = await apiClient.get(`/crm/dashboard/export?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error exporting dashboard data:', error);
      throw error;
    }
  }

  // ===================================
  // CONFIG ENDPOINTS (/api/crm/config)
  // ===================================

  async testAssignment(data) {
    try {
      const response = await apiClient.post('/crm/config/test-assignment', data);
      return response.data.data;
    } catch (error) {
      console.error('Error testing assignment:', error);
      throw error;
    }
  }

  // ===================================
  // APPROVAL CONFIG ENDPOINTS (/api/crm/config/approval-*)
  // ===================================

  async getApprovalConfigs() {
    try {
      const response = await apiClient.get('/crm/config/approval-configs');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching approval configs:', error);
      throw error;
    }
  }

  async getApprovalConfigBySubcategory(subcategoryId) {
    try {
      const response = await apiClient.get(`/crm/config/approval-configs/${subcategoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching approval config:', error);
      throw error;
    }
  }

  async getApprovalRoles() {
    try {
      const response = await apiClient.get('/crm/config/approval-roles');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching approval roles:', error);
      throw error;
    }
  }

  async saveApprovalConfig(configData) {
    try {
      const response = await apiClient.post('/crm/config/approval-configs', configData);
      return response.data.data;
    } catch (error) {
      console.error('Error saving approval config:', error);
      throw error;
    }
  }

  async deleteApprovalConfig(subcategoryId) {
    try {
      const response = await apiClient.delete(`/crm/config/approval-configs/${subcategoryId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting approval config:', error);
      throw error;
    }
  }

  // ===================================
  // TEAM MANAGEMENT
  // ===================================

  async createTeam(teamData) {
    try {
      const response = await apiClient.post('/crm/teams', teamData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating team:', error);
      throw new Error('Team creation will be available in Phase 2');
    }
  }

  async updateTeam(teamId, teamData) {
    try {
      const response = await apiClient.put(`/crm/teams/${teamId}`, teamData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating team:', error);
      throw new Error('Team update will be available in Phase 2');
    }
  }

  async deleteTeam(teamId) {
    try {
      const response = await apiClient.delete(`/crm/teams/${teamId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting team:', error);
      throw new Error('Team deletion will be available in Phase 2');
    }
  }

  async createCategory(categoryData) {
    try {
      const response = await apiClient.post('/crm/categories', categoryData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  async updateCategory(categoryId, categoryData) {
    try {
      const response = await apiClient.put(`/crm/categories/${categoryId}`, categoryData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  async deleteCategory(categoryId) {
    try {
      const response = await apiClient.delete(`/crm/categories/${categoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  async createSubcategory(subcategoryData) {
    try {
      const response = await apiClient.post('/crm/subcategories', subcategoryData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating subcategory:', error);
      throw error;
    }
  }

  async updateSubcategory(subcategoryId, subcategoryData) {
    try {
      const response = await apiClient.put(`/crm/subcategories/${subcategoryId}`, subcategoryData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating subcategory:', error);
      throw error;
    }
  }

  async deleteSubcategory(subcategoryId) {
    try {
      const response = await apiClient.delete(`/crm/subcategories/${subcategoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      throw error;
    }
  }

  // ===================================
  // TICKET UPDATE ENDPOINT
  // ===================================

  async updateTicket(ticketNumber, updateData) {
    try {
      const response = await apiClient.put(`/crm/tickets/${ticketNumber}`, updateData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating ticket:', error);
      throw error;
    }
  }

  // ===================================
  // COMMENTS ENDPOINTS
  // ===================================

  async getTicketComments(ticketNumber) {
    try {
      // Comments are part of ticket history - filter for comment type entries
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}/history`);
      const history = response.data.data || [];
      // Filter to only show comment-type entries if needed
      return history.filter(entry => entry.type === 'comment' || entry.action_type === 'comment');
    } catch (error) {
      console.error('Error fetching ticket comments:', error);
      throw error;
    }
  }

  async addTicketComment(ticketNumber, commentData) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/comment`, {
        comment: commentData.comment || commentData.content || commentData,
        type: commentData.type || 'comment'
      });
      return response.data;
    } catch (error) {
      console.error('Error adding ticket comment:', error);
      throw error;
    }
  }

  // ===================================
  // HISTORY ENDPOINTS
  // ===================================

  async getTicketHistory(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}/history`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket history:', error);
      throw error;
    }
  }

  // ===================================
  // INTERNAL NOTES ENDPOINTS
  // ===================================

  async getInternalNotes(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}/notes`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching internal notes:', error);
      throw error;
    }
  }

  async addInternalNote(ticketNumber, noteData) {
    try {
      const userEmail = localStorage.getItem('userEmail') || 'system@cafuamanagement.com';
      const userName = localStorage.getItem('userName') || 'System';

      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/notes`, {
        note_content: noteData.note_content || noteData.content || noteData,
        created_by_email: noteData.created_by_email || userEmail,
        created_by_name: noteData.created_by_name || userName,
        tagged_users: noteData.tagged_users || []
      });
      return response.data.data;
    } catch (error) {
      console.error('Error adding internal note:', error);
      throw error;
    }
  }

  async updateInternalNote(ticketNumber, noteId, noteContent) {
    try {
      const response = await apiClient.put(`/crm/tickets/${ticketNumber}/notes/${noteId}`, {
        note_content: noteContent
      });
      return response.data.data;
    } catch (error) {
      console.error('Error updating internal note:', error);
      throw error;
    }
  }

  async deleteInternalNote(ticketNumber, noteId) {
    try {
      const response = await apiClient.delete(`/crm/tickets/${ticketNumber}/notes/${noteId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting internal note:', error);
      throw error;
    }
  }

  // ===================================
  // WARNING TEMPLATE ENDPOINTS (/api/crm/warning-templates)
  // ===================================

  /**
   * Get all active warning templates for dropdown selection
   * @returns {Array} Array of warning templates
   */
  async getWarningTemplates() {
    try {
      const response = await apiClient.get('/crm/warning-templates');
      return response.data.data.templates;
    } catch (error) {
      console.error('Error fetching warning templates:', error);
      throw error;
    }
  }

  /**
   * Get template with all fields (header + body) for form rendering
   * @param {number} templateId - The template ID
   * @returns {Object} Template data with header_fields, body_fields, and dropdown_options
   */
  async getWarningTemplateFields(templateId) {
    try {
      const response = await apiClient.get(`/crm/warning-templates/${templateId}/fields`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching warning template fields:', error);
      throw error;
    }
  }

  /**
   * Validate warning field data before ticket submission
   * @param {number} templateId - The template ID
   * @param {Object} fieldData - The field values to validate
   * @returns {Object} Validation result { valid: boolean, errors: Array }
   */
  async validateWarningFields(templateId, fieldData) {
    try {
      const response = await apiClient.post('/crm/warning-templates/validate', {
        template_id: templateId,
        field_data: fieldData
      });
      return response.data.data;
    } catch (error) {
      console.error('Error validating warning fields:', error);
      throw error;
    }
  }

  /**
   * Check if a subcategory requires a warning template
   * @param {number} subcategoryId - The subcategory ID
   * @returns {boolean} Whether warning template is required
   */
  async checkWarningTemplateRequired(subcategoryId) {
    try {
      const response = await apiClient.get(`/crm/warning-templates/check-required/${subcategoryId}`);
      return response.data.data.warning_template_required;
    } catch (error) {
      console.error('Error checking warning template requirement:', error);
      return false; // Default to not required on error
    }
  }

  /**
   * Get warning data for a specific ticket
   * @param {number} ticketId - The ticket ID
   * @returns {Object} Warning data including document URL
   */
  async getTicketWarningData(ticketId) {
    try {
      const response = await apiClient.get(`/crm/warning-templates/ticket/${ticketId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket warning data:', error);
      return null;
    }
  }

  /**
   * Update warning field data for a ticket
   * @param {number} ticketId - The ticket ID
   * @param {Object} warningFieldData - Updated field values
   * @returns {Object} Updated warning data
   */
  async updateTicketWarningData(ticketId, warningFieldData) {
    try {
      const response = await apiClient.patch(`/crm/warning-templates/ticket/${ticketId}/fields`, {
        warning_field_data: warningFieldData
      });
      return response.data.data;
    } catch (error) {
      console.error('Error updating ticket warning data:', error);
      throw error;
    }
  }

  // ===================================
  // RISK MANAGEMENT TEMPLATE ENDPOINTS (/api/crm/rm-templates)
  // ===================================

  /**
   * Check if a subcategory requires an RM template
   * @param {number} subcategoryId
   * @returns {Object} { rm_template_required, rm_template_id }
   */
  async checkRmTemplateRequired(subcategoryId) {
    try {
      const response = await apiClient.get(`/crm/rm-templates/check-required/${subcategoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error checking RM template requirement:', error);
      return { rm_template_required: false, rm_template_id: null };
    }
  }

  /**
   * Get RM template with sections and fields
   * @param {number} templateId
   * @param {Object} [options]
   * @param {string} [options.context] - 'create' to limit to Phase 1 sections only
   * @returns {Object} { template, sections, downloadable_forms }
   */
  async getRmTemplateFields(templateId, { context } = {}) {
    try {
      const params = context ? { context } : {};
      const response = await apiClient.get(`/crm/rm-templates/${templateId}/fields`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching RM template fields:', error);
      throw error;
    }
  }

  /**
   * Validate RM field data
   * @param {number} templateId
   * @param {Object} fieldData
   * @param {number} phase - 1 or 2
   * @returns {Object} { valid, errors }
   */
  async validateRmFields(templateId, fieldData, phase = 1) {
    try {
      const response = await apiClient.post('/crm/rm-templates/validate', {
        template_id: templateId,
        field_data: fieldData,
        phase
      });
      return response.data.data;
    } catch (error) {
      console.error('Error validating RM fields:', error);
      throw error;
    }
  }

  /**
   * Get RM data for a ticket
   * @param {number} ticketId
   * @returns {Object} RM template data for the ticket
   */
  async getTicketRmData(ticketId) {
    try {
      const response = await apiClient.get(`/crm/rm-templates/ticket/${ticketId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket RM data:', error);
      return null;
    }
  }

  /**
   * Update RM field data for a ticket (Phase 1)
   * @param {number} ticketId
   * @param {Object} rmFieldData
   * @returns {Object} Updated data
   */
  async updateTicketRmData(ticketId, rmFieldData) {
    try {
      const response = await apiClient.patch(`/crm/rm-templates/ticket/${ticketId}/fields`, {
        rm_field_data: rmFieldData
      });
      return response.data.data;
    } catch (error) {
      console.error('Error updating ticket RM data:', error);
      throw error;
    }
  }

  /**
   * Save/update Phase 2 addendum data (WC only)
   * @param {number} ticketId
   * @param {Object} addendumData
   * @returns {Object} Updated data
   */
  async updateTicketRmAddendum(ticketId, addendumData) {
    try {
      const response = await apiClient.patch(`/crm/rm-templates/ticket/${ticketId}/addendum`, {
        rm_addendum_data: addendumData
      });
      return response.data.data;
    } catch (error) {
      console.error('Error updating ticket RM addendum:', error);
      throw error;
    }
  }

  /**
   * Generate RM document and upload to SharePoint
   * @param {number} ticketId
   * @returns {Object} { document_url, folder_name, file_name, addendum_url }
   */
  async generateRmDocument(ticketId) {
    try {
      const response = await apiClient.post(`/crm/rm-templates/ticket/${ticketId}/generate-document`);
      return response.data.data;
    } catch (error) {
      console.error('Error generating RM document:', error);
      throw error;
    }
  }

  /**
   * Generate (or regenerate) warning document and upload to SharePoint
   * @param {number} ticketId
   * @returns {Object} { document_url, folder_name, file_name }
   */
  async generateWarningDocument(ticketId) {
    try {
      const response = await apiClient.post(`/crm/warning-templates/ticket/${ticketId}/generate-document`);
      return response.data.data;
    } catch (error) {
      console.error('Error generating warning document:', error);
      throw error;
    }
  }

  /**
   * Get download URL for a downloadable form template
   * @param {string} templateKey - e.g., 'customer_statement', 'witness_statement'
   * @returns {string} Download URL
   */
  getRmFormDownloadUrl(templateKey) {
    return `/api/crm/rm-templates/downloads/${templateKey}`;
  }

  /**
   * Download a downloadable form template (with auth)
   * @param {string} templateKey - e.g., 'customer_statement', 'witness_statement'
   * @param {string} fileName - suggested file name for download
   */
  async downloadRmForm(templateKey, fileName) {
    try {
      const response = await apiClient.get(
        `/crm/rm-templates/downloads/${templateKey}`,
        { responseType: 'blob' }
      );

      // Extract filename from Content-Disposition header if available
      const disposition = response.headers['content-disposition'];
      let downloadName = fileName || `${templateKey}.pdf`;
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) downloadName = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading RM form:', error);
      throw error;
    }
  }

  // ===================================
  // TEAM FIELD SETTINGS ENDPOINTS (/api/crm/settings)
  // ===================================

  /**
   * Get field settings for a team by team code
   * Used by TicketForm to apply visibility/auto-value rules
   * @param {string} teamCode - e.g., 'RM', 'HR', 'CB'
   * @returns {Array} field settings or empty array (default behavior)
   */
  async getTeamFieldSettings(teamCode) {
    try {
      const response = await apiClient.get(`/crm/settings/team-fields/by-code/${teamCode}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching team field settings:', error);
      throw error;
    }
  }

  /**
   * Get field settings for a team by team ID (settings page)
   * @param {number} teamId
   */
  async getTeamFieldSettingsById(teamId) {
    try {
      const response = await apiClient.get(`/crm/settings/team-fields/${teamId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching team field settings by id:', error);
      throw error;
    }
  }

  /**
   * Bulk upsert field settings for a team
   * @param {number} teamId
   * @param {Array} settings - array of { field_key, is_visible, auto_value_source, static_value, is_readonly }
   */
  async updateTeamFieldSettings(teamId, settings) {
    try {
      const response = await apiClient.put(`/crm/settings/team-fields/${teamId}`, { settings });
      return response.data.data;
    } catch (error) {
      console.error('Error updating team field settings:', error);
      throw error;
    }
  }

  /**
   * Get list of all teams for settings page dropdown
   */
  async getTeamsListForSettings() {
    try {
      const response = await apiClient.get('/crm/settings/teams-list');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching teams list:', error);
      throw error;
    }
  }

  // ===================================
  // CONVERSATION & ATTACHMENT ENDPOINTS
  // ===================================

  async getTicketConversations(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}/conversations`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  async submitRequesterReply(ticketNumber, formData) {
    try {
      const response = await apiClient.post(
        `/crm/tickets/${ticketNumber}/requester-reply`,
        formData,
        { headers: { 'Content-Type': undefined } }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error submitting requester reply:', error);
      throw error;
    }
  }

  async uploadTicketAttachments(ticketNumber, formData) {
    try {
      const response = await apiClient.post(
        `/crm/tickets/${ticketNumber}/attachments`,
        formData,
        { headers: { 'Content-Type': undefined } }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error uploading attachments:', error);
      throw error;
    }
  }

  async getTicketAttachments(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}/attachments`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching attachments:', error);
      throw error;
    }
  }

  async deleteTicketAttachment(ticketNumber, attachmentId) {
    try {
      const response = await apiClient.delete(`/crm/tickets/${ticketNumber}/attachments/${attachmentId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw error;
    }
  }

  async downloadTicketAttachment(ticketNumber, attachmentId, fileName) {
    try {
      const response = await apiClient.get(
        `/crm/tickets/${ticketNumber}/attachments/${attachmentId}/download`,
        { responseType: 'blob' }
      );
      // Trigger browser download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'attachment');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  }

  async regenerateDocument(ticketNumber) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/regenerate-document`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error regenerating document:', error);
      throw error;
    }
  }

  // ===================================
  // L&D CHECKLIST METHODS
  // ===================================

  async getChecklistItems(subcategoryId) {
    try {
      const response = await apiClient.get(`/crm/checklist/items/${subcategoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching checklist items:', error);
      throw error;
    }
  }

  async getChecklistResponses(ticketId) {
    try {
      const response = await apiClient.get(`/crm/checklist/responses/${ticketId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching checklist responses:', error);
      throw error;
    }
  }

  async saveChecklistResponses(ticketId, responses) {
    try {
      const response = await apiClient.post(`/crm/checklist/responses/${ticketId}`, { responses });
      return response.data.data;
    } catch (error) {
      console.error('Error saving checklist responses:', error);
      throw error;
    }
  }

  async getStoreLeadership(storePC) {
    try {
      const response = await apiClient.get(`/crm/checklist/store-leadership/${storePC}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching store leadership:', error);
      throw error;
    }
  }

  // ===================================
  // LEGACY METHODS (for backward compatibility)
  // ===================================

  async getDashboardStats(period = 'week') {
    return this.getDashboardSummary({ period });
  }

  async getCategoryBreakdown(period = 'last30days') {
    return this.getDashboardSummary({ period });
  }

  async getWeeklyTrends(period = 'last30days') {
    return this.getTicketTrends();
  }

  async getPriorityDistribution(period = 'last30days') {
    return this.getDashboardSummary({ period });
  }
}

export const crmDataService = new CRMDataService();